import { Cookies } from 'react-cookie';

import qs from 'qs';
import axios from 'axios';

// cs: add to requests user browser timezone
axios.defaults.headers['timezone'] =
    Intl.DateTimeFormat().resolvedOptions().timeZone;

// mm: will be catch to redirect user to login page
axios.defaults.headers['X-sessionExpireCode'] = 401;

import { isPositiveInteger } from '~/modules/helpers/math';
import { rejectPageRequest } from '~/modules/helpers/error';
import { redirectToLoginPage } from '~/modules/helpers/navigation';

import {
    addTrailingSlash,
    removeTrailingSlash
} from '~/modules/helpers/string';

const ASID_COOKIE_NAME = 'ASID';
const APISESSION_COOKIE_NAME = 'apisession';
const LAST_ACTIVITY_COOKIE_NAME = 'LAMS';

const onRequest = config => {
    const url = config.url.startsWith('http') ? config.url : config.baseURL;
    const prefix = APP_URL.django;
    const publicApi = APP_URL.publicApi;
    const djangoEndpoints = [
        'api',
        'automation',
        'chatbot',
        'app-central',
        'training'
        // ark
    ];
    if (
        // these HTTP methods do not require CSRF protection (from django docs)
        djangoEndpoints.some(
            suffix =>
                url.startsWith(`${prefix}/${suffix}`) ||
                url.startsWith(publicApi)
        )
    ) {
        const cookies = new Cookies();
        config.headers[APISESSION_COOKIE_NAME] = cookies.get(ASID_COOKIE_NAME);
    }
    // https://axios-http.com/docs/config_defaults
    return config;
};

/**
 * @param {Object} response
 * @returns {void}
 */
const refreshAuthCookies = response => {
    if (response && response.status === 401) {
        return;
    }
    const cookies = new Cookies();
    const asidCookieValue = cookies.get(ASID_COOKIE_NAME);
    const lamsCookieValue = cookies.get(LAST_ACTIVITY_COOKIE_NAME);

    let cipSessionTimeout = CURRENT_USER.hasOwnProperty('session_timeout')
        ? CURRENT_USER.session_timeout
        : '60';
    // lzanetti: invalid or negative value, default to 60 (1h)
    if (parseInt(cipSessionTimeout) < 0) {
        cipSessionTimeout = 60;
    }
    // lzanetti: max cip value is 7 days
    if (parseInt(cipSessionTimeout) > 60 * 24 * 7) {
        cipSessionTimeout = 60 * 24 * 7;
    }
    // lzanetti: calculate minutes in milliseconds
    const cookieSessionTimeout = cipSessionTimeout * 60 * 1000;
    const cookieExpirationTime = new Date(
        new Date().getTime() + cookieSessionTimeout
    ); // 60 * 60 * 1000 = 3600000 (1 hour as ms)

    if (asidCookieValue) {
        cookies.set(ASID_COOKIE_NAME, asidCookieValue, {
            expires: cookieExpirationTime,
            name: ASID_COOKIE_NAME,
            path: '/',
            secure: true
        });
    }

    if (lamsCookieValue) {
        cookies.set(LAST_ACTIVITY_COOKIE_NAME, new Date().getTime(), {
            expires: cookieExpirationTime,
            name: LAST_ACTIVITY_COOKIE_NAME,
            path: '/',
            secure: true
        });
    }
};

/**
 * @param {Object} response - The HTTP response object.
 * @param {*} response.data
 * @param {number} response.status code in the 200 range
 * @param {string} response.statusText
 * @param {Object} response.headers
 * @param {Object} response.config
 * @param {Object} response.request
 * @returns {Object} response
 */
const onSuccessResponse = response => {
    refreshAuthCookies(response);
    return response;
};

/**
 * @typedef {Object} axios-error
 * @property {string} error.name
 * @property {string} error.message
 * @property {Object} error.config
 * @property {string} error.code
 * @property {number} error.status
 * @property {Object} error.request
 * @property {Object} error.response
 * @property {number} error.response.status
 * @property {string} error.response.statusText
 * @property {Object} error.response.headers
 * @property {Object} error.response.config
 * @property {Object} error.response.request
 * @property {Object} error.response.data
 * @property {string} error.response.data.detail
 * @property {string} error.response.data.suggestedIncidentId optional
 * @returns {Promise<never>} - A rejected Promise with the given error.
 */
const onErrorResponse = error => {
    refreshAuthCookies(error?.response);
    const status = error?.response?.status;
    if (status) {
        switch (status) {
            // case 400: ValidationError, ParseError
            case 401: // unauthorized ( X-sessionExpireCode )
                // vo: forward user to Sumo login
                redirectToLoginPage();
                break;
            // case 403: PermissionDenied
            // case 404: NotFound
            // case 405: MethodNotAllowed
            // case 406: NotAcceptable
            // case 415: UnsupportedMediaType
            case 418: // teapot
                // fm: remove client token
                window.localStorage.removeItem('session_login');
                // todo: redirect should be revised
                // mm: redirect to login to avoid js errors
                const location = window.location;
                const loginPath = '/ui/login/';
                if (!location.href.includes(loginPath)) {
                    location.href = `${APP_URL.django}${loginPath}${location.hash}`;
                }
                break;
            // case 429: Throttled
            // case 500: APIException
            default:
                if (APP_INFO.debug) {
                    console.debug(error);
                }
        }
    }
    return Promise.reject(error);
};

const setupInterceptors = axiosObj => {
    const axiosObjInterceptors = axiosObj.interceptors;
    axiosObjInterceptors.response.use(onSuccessResponse, onErrorResponse);
    axiosObjInterceptors.request.use(onRequest);
};

setupInterceptors(axios);

export const skipStatusHeader = Object.freeze({
    'X-CSOAR-SKIPSTATUS': 'toskip'
});

export const toOrderBy = sortModel => {
    return sortModel.reduce((obj, colDef) => {
        obj[colDef.colId || colDef.field] = colDef.sort; // 'desc'|'asc'
        return obj;
    }, {});
};

/**
 * Class representing a generic entity used to interact with the server
 * Static method are useful when the request doesn't refer to a specific entity
 * All field that start with _underscore will be ignored
 * @author mm
 */
class Model {
    /**
     * The cached axios instance
     * @type {AxiosInstance}
     * @private
     */
    static _axiosInstance;

    /**
     * Retrieve the cached axios instance when available or create one
     * @returns {AxiosInstance}
     */
    static get api() {
        const cachedAxios = this._axiosInstance;
        if (cachedAxios) {
            return cachedAxios;
        } else {
            // prettier-ignore
            const axiosInstance = (
              this._axiosInstance = axios.create({
                    baseURL: this._url
                })
            );
            setupInterceptors(axiosInstance);
            return axiosInstance;
        }
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * The API base url
     * @type {string}
     * @private
     */
    static _url = `${APP_URL.django}/api`;

    /**
     * chatBot url
     * @type {string}
     * @private
     */
    static _chatBot = `${APP_URL.django}/chatbot`;

    /**
     * Marketplace url
     * @type {string}
     * @private
     */
    static _marketplaceUrl = `${APP_URL.django}/app-central`;

    /**
     * Public API base url
     * @type {string}
     * @private
     */
    static _publicApiUrl = APP_URL.publicApi;

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Get details of one entity defined by the id
     * @param {number} id - The entity identifier
     * @param {Object} params - optional parameters
     * @param {boolean} ws_invoke - truthy when request is triggered by websocket
     * @returns {AxiosPromise<any>}
     */
    static get(id, params, ws_invoke) {
        const requestPayload = { params: params ?? {} };
        const requestUrl = `${this._url}/${id}/`;
        const config = {};
        if (ws_invoke) {
            config.headers = { ...skipStatusHeader };
        }
        return this.api.get(requestUrl, requestPayload, config);
    }

    /**
     * Get a list of entities
     * @param {Object} params - Optional params to append to the call
     * @returns {AxiosPromise<any>}
     */
    static all(params) {
        const { url = this._url, ...rest } = params || {};
        const requestPayload = { params: rest };
        const requestUrl = addTrailingSlash(url);
        return this.api.get(requestUrl, requestPayload);
    }

    /**
     * @param {Object} params
     * @returns {AxiosPromise<any>}
     */
    static delete(params) {
        const requestPayload = { params: params ?? {} };
        const requestUrl = `${this._url}`;
        return this.api.delete(requestUrl, requestPayload);
    }

    /**
     * @param {Object} params
     * @returns {AxiosPromise<any>}
     */
    static restore(params) {
        const requestPayload = { params: params ?? {} };
        const requestUrl = `${this._url}`;
        return this.api.patch(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * The legacy url for not standard API call
     * @type {string}
     * @private
     * @deprecated
     */
    static _legacyUrl = `${APP_URL.root}/lib/lib`;

    /**
     * The legacy url for not standard API call into report dir
     * @type {string}
     * @private
     * @deprecated
     */
    static _legacyReportUrl = `${APP_URL.root}/lib/reports`;

    /**
     * The legacy url for not standard API call
     * @type {string}
     * @private
     * @deprecated
     */
    static _legacyGuiUrl = `${APP_URL.root}/lib/gui`;

    /**
     * @description Static function to call a php file that doesn't have token and op_type
     * @param params
     * @param endpoint
     * @param gui
     * @param report
     * @return {AxiosPromise<any>}
     * @author fm
     * @deprecated
     */
    static legacyCallToFile(params, endpoint, gui = false, report = false) {
        let url = this._legacyUrl;
        if (endpoint) {
            if (gui) {
                url = `${Model._legacyGuiUrl}/${endpoint}.php`;
            } else if (report) {
                url = `${Model._legacyReportUrl}/${endpoint}.php`;
            } else {
                url = `${Model._legacyUrl}/${endpoint}.php`;
            }
        }
        if (!report) {
            const urlParams = qs.stringify(params);
            return axios.post(url, urlParams);
        }
        return axios.get(url, params);
    }

    /**
     * @description Static function to call a php file that requires uploadify as a library
     * @params FormData
     * @opType op_type
     * @header multipart
     * @return {AxiosPromise<any>}
     * @author ab
     * @deprecated
     */
    static multipartLegacyCall(params, opType, header) {
        let url = this._legacyUrl;
        const tokenInput = document.getElementById(opType);
        params.append('form_token', tokenInput.value);
        const promise = axios.post(url, params, header);
        promise.then(res => {
            tokenInput.value = res.data.token;
        });
        return promise;
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * When instantiated the class represent a specific entity identified by the id
     * @param {null} id - The entity numeric identifier
     * @param {object} options
     * @author fm | mm
     */
    constructor(id = null, options = {}) {
        this.id = id;
        this.params = {};
        this.options = {
            loadData: false,
            ...options
        };
        if (this.options.loadData) {
            return this._load();
        }
    }

    /**
     * Load entity data as instance properties
     * @returns {AxiosPromise<any>}
     */
    _load() {
        const id = this.id;
        if (id) {
            return this.constructor.get(id).then(response => {
                Object.assign(this, response.data);
            });
        } else {
            throw new Error(
                "Can't load the entity: the 'id' instance property is not set/valid"
            );
        }
    }

    /**
     * @return {AxiosPromise<any>}
     * @author mm | fm
     */
    save() {
        const requestPayload = {};
        for (const [key, value] of Object.entries(this)) {
            if (key.startsWith('_')) {
                requestPayload[key] = value;
            }
        }
        const constructor = this.constructor;
        const url = constructor._url;
        const id = this.id;
        if (id) {
            // update:
            const requestUrl = `${url}/${id}/`;
            return constructor.api.put(requestUrl, requestPayload);
        } else {
            // create:
            const requestUrl = `${url}/`;
            return constructor.api.post(requestUrl, requestPayload);
        }
    }

    /**
     * @param {int} number - The page number from the pagination
     * @param {int} size - The number of row to fetch
     * @param {Array<{colId: string, sort: string}>} sortModel - criteria
     * @param {String} filterString - The filter query
     * @param {Array<string>} visibleColumns - fields of visible columns
     * @param {Boolean} count - perform and return count of total rows
     * @param {Boolean} retrieveAllOccurrences
     * @param {int}  occurrenceNumber result
     * @param {Boolean} ws_invoke - if request is invoked by web socket message
     * @returns {Promise<never>|void|*}
     */
    static requestPage(
        number,
        size,
        sortModel,
        filterString,
        visibleColumns,
        count,
        retrieveAllOccurrences,
        occurrenceNumber,
        ws_invoke
    ) {
        const isValidPageNumber = isPositiveInteger(number);
        const isValidPageSize = isPositiveInteger(size);
        if (isValidPageNumber && isValidPageSize) {
            const requestPayload = {
                page_number: number,
                page_size: size,
                count: count ?? false,
                order_by: toOrderBy(sortModel)
            };
            if (visibleColumns?.length) {
                requestPayload.columns = visibleColumns;
            }
            if (filterString?.length) {
                requestPayload.filter = filterString;
            }
            if (retrieveAllOccurrences) {
                requestPayload.all = true;
            }
            /*
            if (occurrenceNumber) {
                params.occurrenceNumber = occurrenceNumber;
            }
            */
            // lz: if request triggered by websocket, send skipstatus header
            const requestConfig = {};
            if (ws_invoke) {
                requestConfig.headers = { ...skipStatusHeader };
            }
            const requestUrl = `${removeTrailingSlash(this._url)}/search/`;
            return this.api.post(requestUrl, requestPayload, requestConfig);
        } else {
            return rejectPageRequest({
                number,
                isValidPageNumber,
                size,
                isValidPageSize
            });
        }
    }

    /**
     * Requests the count of deleted items based on the provided filter.
     * @param {string} params.filter - The filter to apply for counting deleted items.
     * @param {string} params.message - The message template to display with count information.
     * @returns {Object} An object containing the message and a promise for requesting the count.
     */
    static requestDeletedCount(params) {
        params ||= {};
        return {
            message:
                params.message ||
                'Of the last {total} {items}, {count} are deleted and not reported',
            promise: this.requestPage(
                1, // pageNum*
                1, // rowsPerPage*
                [], // sortModel
                params.filter || 'deleted=true', // filterString*
                [], // visibleColumns
                true, // count*
                false, // retrieveAllOccurrences*
                null, // occurrenceNumber
                false // ws_invoke*
            )
        };
    }
}

export default Model;
